<template>
  <div class="admin-container">
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :active-tab="currentTab"
      @toggle="toggleSidebar"
      @switch-tab="switchTab"
    />

    <!-- Toast 提示 -->
    <transition name="toast-fade">
      <div v-if="toast.show" :class="['toast', 'toast-' + toast.type]" @click="toast.show = false">
        {{ toast.message }}
      </div>
    </transition>

    <main class="content">
      <div class="page-header">
        <h1 class="content-title">{{ currentContentTitle }}</h1>

        <div class="page-actions" v-if="currentTab === 'finance-bank'">
          <button @click="handleManualSyncFinanceBank" class="btn btn-sync" :disabled="isFinanceBankSyncing">
            <span v-if="isFinanceBankSyncing" class="loading-spinner">
              <svg class="spinner" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                  <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                  <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
                </circle>
              </svg>
              同步中...
            </span>
            <span v-else>同步数据</span>
          </button>
        </div>
      </div>

      <ReportManager
        v-if="currentTab === 'reports'"
        :reports="reportsData"
        :is-loading="isReportsLoading"
        @delete-report="handleDeleteInReports"
        @replace-report="openUploadModalInReports"
        @open-upload-modal="() => openUploadModalInReports(null)"
        @move-report="handleMoveReport"
      />

      <CrawlerManager
        v-else-if="currentTab.startsWith('crawler-')"
        :active-panel="currentTab"
        @switch-crawler-tab="switchTab"
      />

      <PublishManager
        v-else-if="currentTab === 'publish'"
      />

      <DataManager
        ref="dataManager"
        v-else
        :is-loading="tabLoadingState[currentTab] || tableLoadingState[currentTab] || false"
        :paged-data="tableData"
        :headers="processedTableHeaders"
        :unique-column-values="uniqueColumnValues"
        :last-update-time="formattedLastUpdate"
        :current-page="currentPage"
        :page-size="pageSize"
        :total-rows="tableDataTotalRows"
        :is-upload-visible="currentTab !== 'finance-bank'"
        :active-filters="activeFilters"
        :search-keyword="searchKeyword"
        :skip-audit-log="currentTab === 'finance-bank'"
        :is-filter-loading="filterLoadingState[currentTab] || false"
        :is-export-loading="exportLoadingState[currentTab] || false"
        :is-page-loading="pageLoadingState[currentTab] || false"
        :is-prev-loading="prevLoadingState[currentTab] || false"
        :is-next-loading="nextLoadingState[currentTab] || false"
        :is-jump-loading="jumpLoadingState[currentTab] || false"
        @search-input="handleSearch"
        @apply-filters="handleApplyFilter"
        @clear-all-filters="clearAllFilters"
        @page-changed="changePage"
        @cell-updated="handleCellUpdated"
        @row-deleted="handleRowDeleted"
        @upload-excel="handleExcelUpload"
        @export-excel="exportExcel"
      />
    </main>

    <PdfReportUpload
      :visible="isUploadModalVisible"
      :file-to-replace="reportToReplace"
      @close="() => { 
        isUploadModalVisible = false;
        reportToReplace = null;
      }"
      @upload-success="handleReportUploadSuccess"
    />

    <ExcelUploadWizard
      :visible="excelUploadWizardVisible"
      :table-name="tableNameMap[currentTab] || ''"
      :tab-label="currentContentTitle"
      :is-edit-mode="false"
      :initial-file="excelUploadPendingFile"
      @close="closeExcelUploadWizard"
      @success="handleExcelUploadSuccess"
      @error="(msg) => showToast(msg, 'error')"
    />
  </div>
</template>

<script>
import AdminSidebar from '@/components/admin/AdminSidebar.vue';
import ReportManager from '@/components/admin/ReportManager.vue';
import DataManager from '@/components/admin/DataManager.vue';
import PdfReportUpload from '@/components/admin/PdfReportUpload.vue';
import CrawlerManager from '@/components/admin/CrawlerManager.vue';
import PublishManager from '@/components/admin/PublishManager.vue';
import ExcelUploadWizard from '@/components/admin/ExcelUploadWizard.vue';
import * as adminService from '@/services/adminApiService.js';
import { updateCell } from '@/services/adminApiService.js';
import { reportManagerMixin } from '@/utils/reportManagerMixin.js';
import { dataTableMixin } from '@/utils/dataTableMixin.js';
import { toastMixin } from '@/utils/toastMixin.js';

export default {
  name: 'AdminPage',
  mixins: [reportManagerMixin, dataTableMixin, toastMixin],
  components: {
    AdminSidebar,
    ReportManager,
    DataManager,
    PdfReportUpload,
    CrawlerManager,
    PublishManager,
    ExcelUploadWizard,
  },
  data() {
    return {
      currentTab: 'listed',
      isSidebarCollapsed: false,
      tableData: [],
      activeFilters: {}, // 重新添加
      pageSize: 20, // 每页显示20条内容
      currentPage: 1, // 重新添加
      totalRows: 0, // 重新添加
      filterOptions: {}, // 重新添加
      reports: [], // 重新添加
      reportOrder: [], // 重新添加
      tableNameMap: {
        'listed': 'dataasset_listed_companies',
        'nonlisted': 'dataasset_non_listed_companies',
        'finance-bank': 'dataasset_finance_bank',
        'finance-stock': 'dataasset_finance_stock',
        'finance-other': 'dataasset_finance_other',
        'finance-abs': 'dataasset_finance_abs'
      },
      duplicateKeyConfigs: {
        'dataasset_listed_companies': ['证券代码', '报告时间'],
        'dataasset_non_listed_companies': ['province_area', 'month_time', 'company_name', 'dataasset_content'],
        'dataasset_finance_bank': ['month_time', 'show_name', 'dataasset_content'],
        'dataasset_finance_stock': ['入股时间', '作价入股企业', '数据资产', '入股公司'],
        'dataasset_finance_other': ['融资类型', '日期', '企业'],
        'dataasset_finance_abs': ['数据资产ABS类型', '时间', 'ABS产品']
      },
      searchKeyword: '',
      // 添加缺失的属性
      processedTableHeaders: [],
      uniqueColumnValues: {},
      tableDataTotalRows: 0,
      tableLoadingState: {},
      isUploadModalVisible: false,
      reportToReplace: null,
      // 每张表的独立更新时间
      tableUpdateTimes: {},
      // 分页 loading 状态
      pageLoadingState: {
        'listed': false,
        'nonlisted': false,
        'finance-bank': false,
        'finance-stock': false,
        'finance-other': false,
        'finance-abs': false
      },
      // 上一页按钮 loading 状态
      prevLoadingState: {
        'listed': false,
        'nonlisted': false,
        'finance-bank': false,
        'finance-stock': false,
        'finance-other': false,
        'finance-abs': false
      },
      // 下一页按钮 loading 状态
      nextLoadingState: {
        'listed': false,
        'nonlisted': false,
        'finance-bank': false,
        'finance-stock': false,
        'finance-other': false,
        'finance-abs': false
      },
      // 跳转按钮 loading 状态
      jumpLoadingState: {
        'listed': false,
        'nonlisted': false,
        'finance-bank': false,
        'finance-stock': false,
        'finance-other': false,
        'finance-abs': false
      },
      // tab 切换 loading 状态
      tabLoadingState: {
        'listed': false,
        'nonlisted': false,
        'finance-bank': false,
        'finance-stock': false,
        'finance-other': false,
        'finance-abs': false
      },
      // 筛选操作 loading 状态
      filterLoadingState: {
        'listed': false,
        'nonlisted': false,
        'finance-bank': false,
        'finance-stock': false,
        'finance-other': false,
        'finance-abs': false
      },
      // 导出操作 loading 状态
      exportLoadingState: {
        'listed': false,
        'nonlisted': false,
        'finance-bank': false,
        'finance-stock': false,
        'finance-other': false,
        'finance-abs': false
      },
      isFinanceBankSyncing: false,
      excelUploadWizardVisible: false,
      excelUploadPendingFile: null,
      mappedUploadTabs: ['listed', 'nonlisted', 'finance-stock', 'finance-other', 'finance-abs'],
    };
  },
  computed: {
    currentContentTitle() {
      switch (this.currentTab) {
        case 'listed': return '上市公司数据';
        case 'nonlisted': return '非上市公司数据';
        case 'finance-bank': return '数据资产增信银行贷款';
        case 'finance-stock': return '数据资产作价入股';
        case 'finance-other': return '其他数据类融资';
        case 'finance-abs': return '数据资产ABS';
        case 'reports': return '报告管理';
        case 'crawler-company-info': return '公司信息';
        case 'crawler-task': return '爬取任务';
        case 'crawler-review': return '数据审核';
        case 'publish': return '发布管理';
        default: return '数据明细管理';
      }
    },
    
    formattedLastUpdate() {
      // 使用当前标签页的独立更新时间
      const lastUpdate = this.tableUpdateTimes[this.currentTab];
      if (lastUpdate) {
        return new Date(lastUpdate).toLocaleString('zh-CN');
      }
      return '暂无更新记录';
    }
  },
  methods: {

    handleSearch(keyword) {
      this.searchKeyword = keyword;
      
      // 搜索筛选不需要设置 filterLoadingState，因为不是通过"确定"按钮触发的
      this.loadData();
    },

    toggleSidebar() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    },
    
    // loadReports 方法已经在 reportManagerMixin 中定义，不需要重复定义

    async loadData(isInitialLoad = false) {
      
      // 只在首次加载时设置全屏loading状态
      if (isInitialLoad) {
        this.$set(this.tableLoadingState, this.currentTab, true);
      }
      
      try {
        // 调用API加载表格数据
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          searchKeyword: this.searchKeyword,
          filters: this.activeFilters
        };
        
        const result = await adminService.loadAdminTableData(this.currentTab, params);
        
        this.tableData = result.data || [];
        this.tableDataTotalRows = result.total || 0;
        // 重新排序表头，让时间列优先显示
        this.processedTableHeaders = this.reorderHeaders(result.headers || []);
        
        // 保存当前表的更新时间
        if (result.tableLastUpdate) {
          this.$set(this.tableUpdateTimes, this.currentTab, result.tableLastUpdate);
        }
        
      } catch (error) {
        this.tableData = [];
        this.tableDataTotalRows = 0;
        this.processedTableHeaders = [];
        
        // 显示更详细的错误信息
        const errorMessage = error.response?.data?.error || error.message || '未知错误';
        
        // 可以在这里显示错误提示
        this.showToast(`加载数据失败: ${errorMessage}`, 'error');
      } finally {
        // 只在首次加载时重置全屏loading状态
        if (isInitialLoad) {
          this.$set(this.tableLoadingState, this.currentTab, false);
        }
      }
    },

    async loadFilterOptions() {
      try {
        // 调用API加载筛选选项
        const result = await adminService.loadDistinctColumnValues(this.currentTab);
        this.uniqueColumnValues = result || {};
        
      } catch (error) {
        this.uniqueColumnValues = {};
        this.showToast('加载筛选选项失败: ' + (error.message || '未知错误'), 'error');
      }
    },

    clearAllFilters(shouldReload = true) {
      // 设置筛选 loading 状态（清空筛选操作）
      this.$set(this.filterLoadingState, this.currentTab, true);
      
      this.activeFilters = {};
      this.searchKeyword = '';
      
      if (shouldReload) {
        this.loadData().finally(() => {
          // 重置筛选 loading 状态
          this.$set(this.filterLoadingState, this.currentTab, false);
        });
      } else {
        // 如果不重新加载数据，也要重置 loading 状态
        this.$set(this.filterLoadingState, this.currentTab, false);
      }
    },

    // 报告管理相关方法
    async handleDeleteInReports(id) {
      try {
        await adminService.deleteReport(id);
        this.reportsData = this.reportsData.filter(r => r.id !== id);
        this.showToast('报告已删除', 'success');
      } catch (err) {
        this.showToast(`删除失败: ${err.message}`, 'error');
      }
    },

    openUploadModalInReports(report) {
      this.reportToReplace = report || null;
      this.isUploadModalVisible = true;
    },

    async handleMoveReport({ index, direction }) {
      if (direction === 'up' && index > 0) {
        const temp = this.reportsData[index];
        this.reportsData[index] = this.reportsData[index - 1];
        this.reportsData[index - 1] = temp;
      } else if (direction === 'down' && index < this.reportsData.length - 1) {
        const temp = this.reportsData[index];
        this.reportsData[index] = this.reportsData[index + 1];
        this.reportsData[index + 1] = temp;
      } else {
        return;
      }

      try {
        await adminService.saveReportOrder(this.reportsData.map(r => r.id));
        this.showToast('排序已更新', 'success');
      } catch (err) {
        this.showToast(`排序保存失败: ${err.message}`, 'error');
        this.loadReports();
      }
    },

    handleReportUploadSuccess() {
      this.isUploadModalVisible = false;
      this.loadReports(); // 重新加载报告列表
    },

    async handleManualSyncFinanceBank() {
      if (this.isFinanceBankSyncing || this.currentTab !== 'finance-bank') {
        return;
      }
      this.isFinanceBankSyncing = true;
      try {
        const response = await adminService.manualSyncFinanceBank();
        const message = response?.message || '同步完成';
        this.showToast(message, 'success');
        await this.loadData(true);
      } catch (error) {
        this.showToast(`同步失败: ${error.message}`, 'error');
      } finally {
        this.isFinanceBankSyncing = false;
      }
    },

    // 重新排序表头，让"更新时间"列显示在"更新日志"列的前面
    reorderHeaders(headers) {
      if (!headers || headers.length === 0) return headers;

      // 分离特殊列和普通列
      const normalColumns = [];
      let updatedAtColumn = null;
      let modificationLogColumn = null;

      headers.forEach(header => {
        if (header === 'updated_at') {
          // 找到更新时间列，先保存起来
          updatedAtColumn = header;
        } else if (header === 'modification_log') {
          // 找到更新日志列，先保存起来
          modificationLogColumn = header;
        } else {
          // 其他普通列
          normalColumns.push(header);
        }
      });

      // 重新排序：普通列 + 更新时间列 + 更新日志列
      const reorderedHeaders = [...normalColumns];
      
      if (updatedAtColumn) {
        reorderedHeaders.push(updatedAtColumn);
      }
      
      if (modificationLogColumn) {
        reorderedHeaders.push(modificationLogColumn);
      }

      return reorderedHeaders;
    },

    // 数据管理相关方法
    handleApplyFilter(filterData) {
      this.activeFilters[filterData.column] = filterData.values;
      
      // 设置筛选 loading 状态（列筛选）
      this.$set(this.filterLoadingState, this.currentTab, true);
      
      this.loadData().finally(() => {
        // 重置筛选 loading 状态
        this.$set(this.filterLoadingState, this.currentTab, false);
      });
    },

    changePage(page) {
      // 在设置 currentPage 之前判断翻页方向
      const isPrevPage = page < this.currentPage;
      const isNextPage = page > this.currentPage;
      
      // 根据翻页方向设置对应按钮的 loading 状态
      if (isPrevPage) {
        // 上一页
        this.$set(this.prevLoadingState, this.currentTab, true);
      } else if (isNextPage) {
        // 下一页
        this.$set(this.nextLoadingState, this.currentTab, true);
      }
      
      this.currentPage = page;
      
      // 添加小延迟确保 loading 状态有足够时间显示
      setTimeout(() => {
        this.loadData().finally(() => {
          // 重置所有分页按钮的 loading 状态
          this.$set(this.prevLoadingState, this.currentTab, false);
          this.$set(this.nextLoadingState, this.currentTab, false);
          this.$set(this.jumpLoadingState, this.currentTab, false);
        });
      }, 100);
    },

    async handleCellUpdated({ row, colName, oldValue, newValue }) {
      const tableName = this.tableNameMap[this.currentTab];
      try {
        const result = await updateCell(tableName, row.id, colName, newValue, 'admin');
        if (result.unchanged) return;
        this.$set(row, colName, newValue);
        if (result.newModificationLog !== undefined) {
          this.$set(row, 'modification_log', result.newModificationLog);
        }
        this.showToast('修改已保存', 'success');
      } catch (err) {
        this.$set(row, colName, oldValue);
        this.showToast(`保存失败: ${err.message}`, 'error');
      }
    },

    async handleRowDeleted(row) {
      const tableName = this.tableNameMap[this.currentTab];
      try {
        await adminService.updateRowStatusInDb(tableName, row.id, 'delete');
        this.tableData = this.tableData
          .map(group => ({ ...group, row_group: group.row_group.filter(r => r.id !== row.id) }))
          .filter(group => group.row_group.length > 0);
        this.tableDataTotalRows = Math.max(0, this.tableDataTotalRows - 1);
        this.showToast('已删除', 'success');
      } catch (err) {
        this.showToast(`删除失败: ${err.message}`, 'error');
      }
    },



    exportExcel() {
      // 设置导出 loading 状态
      this.$set(this.exportLoadingState, this.currentTab, true);
      
      const dbTableName = this.tableNameMap[this.currentTab];
      adminService.exportTableToExcel(dbTableName);
      
      // 延迟重置 loading 状态，确保用户能看到 loading 效果
      setTimeout(() => {
        this.$set(this.exportLoadingState, this.currentTab, false);
      }, 2000); // 导出可能需要更长时间
    },

    async saveReportOrder() {
      try {
        await adminService.saveReportOrder(this.reportsData.map(r => r.id));
        this.showToast('报告顺序保存成功！', 'success');
        this.loadReports();
      } catch (err) {
        this.showToast(`报告保存失败: ${err.message}`, 'error');
      }
    },


    async switchTab(tab) {
      if (this.currentTab === tab) return;
      
      // 保存当前标签页到 localStorage
      localStorage.setItem('adminCurrentTab', tab);
      
      // 重置页码到第1页
      this.currentPage = 1;
      
      // 立即设置 tab 切换 loading 状态，提升用户体验
      this.$set(this.tabLoadingState, tab, true);
      
      this.currentTab = tab;

      if (tab === 'reports') {
        this.loadReports();
      } else if (tab.startsWith('crawler-') || tab === 'publish') {
        // 数据爬取/发布管理 Tab，组件自行管理数据加载
        // 不需要在这里加载数据
      } else {
        this.clearAllFilters(false);
        
        // 并行加载筛选选项和数据，不相互依赖
        const [filterOptionsResult, dataResult] = await Promise.allSettled([
          this.loadFilterOptions(),
          this.loadData(true) // 首次加载，传入true
        ]);
        
        // 检查筛选选项加载结果
        if (filterOptionsResult.status === 'rejected') {
          // 筛选选项加载失败不影响数据显示
        }
        
        if (dataResult.status === 'rejected') {
          this.showToast(`加载数据失败: ${dataResult.reason.message || '未知错误'}`, 'error');
        }
      }
      
      // 重置 tab 切换 loading 状态
      this.$set(this.tabLoadingState, tab, false);
    },

    usesMappedExcelUpload() {
      return this.mappedUploadTabs.includes(this.currentTab);
    },

    closeExcelUploadWizard() {
      this.excelUploadWizardVisible = false;
      this.excelUploadPendingFile = null;
      this.$refs.dataManager && this.$refs.dataManager.resetUploadLoading();
    },

    async handleExcelUploadSuccess(response) {
      // 特殊情况：冲突检测阶段就发现全部是完全重复行，wizard 直接关闭
      if (response.skipped) {
        this.showToast(response.message || '数据与库中完全相同，无需操作。', 'info');
        this.closeExcelUploadWizard();
        return;
      }

      if (response.stats) {
        const s = response.stats;
        const parts = [];
        if (s.insertedUnique > 0) parts.push(`新增 ${s.insertedUnique} 条`);
        if (s.replacedWithNewUpload > 0) parts.push(`替换 ${s.replacedWithNewUpload} 条`);
        if (s.keptOldCount > 0) parts.push(`保留旧版 ${s.keptOldCount} 条`);
        if (s.ignoredFullDuplicate > 0) parts.push(`完全重复跳过 ${s.ignoredFullDuplicate} 条`);
        if (s.filteredEmptyRows > 0) parts.push(`过滤空值 ${s.filteredEmptyRows} 条`);

        const summary = parts.length > 0 ? parts.join('，') : '无数据变化';
        this.showToast(`导入完成：${summary}。`, parts.some(p => p.includes('新增') || p.includes('替换')) ? 'success' : 'info');
        this.loadData();
      }
      this.closeExcelUploadWizard();
    },

    async handleExcelUpload(file) {
      if (!file) return;

      const tableName = this.tableNameMap[this.currentTab];
      if (!tableName) {
        this.showToast('错误：找不到当前表名，无法上传数据。', 'error');
        this.$refs.dataManager && this.$refs.dataManager.resetUploadLoading();
        return;
      }

      if (this.usesMappedExcelUpload()) {
        this.excelUploadPendingFile = file;
        this.excelUploadWizardVisible = true;
        return;
      }

      this.showToast('当前 Tab 不支持 Excel 上传。', 'error');
      this.$refs.dataManager && this.$refs.dataManager.resetUploadLoading();
    },

  },

  async mounted() {
    // 检查是否是第一次访问（通过检查是否有特定的标记）
    const isFirstVisit = !localStorage.getItem('adminPageInitialized');
    const savedTab = localStorage.getItem('adminCurrentTab');
    
    if (isFirstVisit) {
      // 第一次访问，强制使用 listed tab
      this.currentTab = 'listed';
      // 标记页面已经初始化过
      localStorage.setItem('adminPageInitialized', 'true');
    } else if (savedTab && ['listed', 'nonlisted', 'finance-bank', 'finance-stock', 'finance-other', 'finance-abs', 'reports', 'crawler-company-info', 'crawler-task', 'crawler-review', 'crawler-sync', 'publish'].includes(savedTab)) {
      // 不是第一次访问，恢复上次选择的标签页（原「数据同步」已合并至数据审核）
      this.currentTab = savedTab === 'crawler-sync' ? 'crawler-review' : savedTab;
    } else {
      // 没有保存的tab，使用默认值
      this.currentTab = 'listed';
    }
    
    // 清理可能存在的旧筛选
    this.clearAllFilters(false);
    
    // 根据当前标签页加载相应的数据
    try {
      if (this.currentTab === 'reports') {
        await this.loadReports();
      } else if (this.currentTab.startsWith('crawler-') || this.currentTab === 'publish') {
        // 数据爬取/发布管理 Tab，组件自行管理数据加载
        // 不需要在这里加载数据
      } else {
        // 并行加载筛选选项和数据，不相互依赖
        const [filterOptionsResult, dataResult] = await Promise.allSettled([
          this.loadFilterOptions(),
          this.loadData(true) // 首次加载，传入true
        ]);
        
        // 检查筛选选项加载结果
        if (filterOptionsResult.status === 'rejected') {
          // 筛选选项加载失败不影响数据显示
        }
        
        if (dataResult.status === 'rejected') {
          throw dataResult.reason; // 数据加载失败要抛出错误
        }
      }
    } catch (error) {
      // 显示错误提示
      this.showToast(`初始化失败: ${error.message || '未知错误'}`, 'error');
    }

  },
};
</script>

<style scoped>
.admin-container {
  display: flex;
  height: 100vh;
  font-family: "Segoe UI", Roboto, sans-serif;
  overflow: hidden;
  background-color: #F5F3F4;
}

.content {
  flex: 1;
  background-color: #ffffff;
  padding: 32px;
  overflow: auto;
  transition: margin-left 0.3s ease;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.content-title {
  color: #2e3968;
  font-size: 24px;
  font-weight: bold;
  margin: 0;
}

.page-actions .btn,
.edit-actions .btn {
  border: 1px solid #2e3968;
  border-radius: 4px;
  padding: 6px 18px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  text-align: center;
}

.edit-actions {
  display: flex;
  gap: 12px;
}

.view-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.btn-edit {
  background-color: #fff;
  color: #2e3968;
}
.btn-edit:hover {
  background-color: #f5f5f5;
}

.btn-save {
  background-color: #2e3968;
  color: #fff;
}
.btn-save:hover {
  opacity: 0.85;
}

.btn-cancel {
  background-color: #f5f5f5;
  color: #333;
  border-color: #ccc;
}
.btn-cancel:hover {
  background-color: #e0e0e0;
}

.btn-sync {
  background-color: #f3f7ff;
  color: #2e3968;
  border-color: #2e3968;
}
.btn-sync:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-sync:hover:not(:disabled) {
  background-color: #e2edff;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spinner circle {
  stroke: currentColor;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

/* Toast 提示 */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  z-index: 9999;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-width: 500px;
}
.toast-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
.toast-error { background: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }
.toast-warning { background: #fff3cd; color: #856404; border: 1px solid #ffeeba; }
.toast-info { background: #d1ecf1; color: #0c5460; border: 1px solid #bee5eb; }

.toast-fade-enter-active, .toast-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.toast-fade-enter, .toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-12px);
}
</style>